import { useState, useCallback, ComponentProps } from "react";
import { useGetExternalPayrunsQuery } from "@xemplo/payrun-query";
import { PAYRUN_STATUS_IDS } from "@xemplo/gp-types";
import PayrunPrepareDetail from "./prepare";

const EXTERNAL_PAYRUNS_PAGE_SIZE = 10;

// Statuses where earning lines are being imported or already imported — the
// external payruns selection list is not shown in these states, so there is
// no need to fetch the list.
const EARNING_LINES_ACTIVE_STATUSES = [
  PAYRUN_STATUS_IDS.IMPORTING_EARNING_LINES_SYSTEM_TRIGGERED,
  PAYRUN_STATUS_IDS.IMPORTING_EARNING_LINES_USER_TRIGGERED,
  PAYRUN_STATUS_IDS.IMPORTED_EARNING_LINES_SYSTEM_TRIGGERED,
  PAYRUN_STATUS_IDS.IMPORTED_EARNING_LINES_USER_TRIGGERED,
];

/**
 * Functional component wrapper so we can use hooks in the prepare class component.
 */
const PayrunPrepareContainer = (
  props: ComponentProps<typeof PayrunPrepareDetail>,
) => {
  const payrunId = props.payrun?.id;
  const [externalPayrunsPage, setExternalPayrunsPage] = useState(1);

  const earninglinesAlreadyImported = EARNING_LINES_ACTIVE_STATUSES.includes(
    props.payrun?.payrunStatusId,
  );

  const {
    data,
    isFetching,
    refetch,
  } = useGetExternalPayrunsQuery({
    // Pass undefined when earning lines are active so the hook stays disabled.
    // The query re-enables automatically when the status changes (e.g. after
    // the user clears earning lines and getPayrun returns a non-import status).
    payrunId: earninglinesAlreadyImported ? undefined : payrunId,
    requestParams: {
      page: externalPayrunsPage,
      per_page: EXTERNAL_PAYRUNS_PAGE_SIZE,
    },
  });

  // Guard the refetch so that calls made while the query is disabled (e.g.
  // cancelExternalPayrun fires refetchExternalPayruns before getPayrun has
  // resolved and updated the status) do not trigger a request with an
  // undefined payrunId.
  const refetchExternalPayruns = useCallback(() => {
    if (!earninglinesAlreadyImported) {
      refetch();
    }
  }, [earninglinesAlreadyImported, refetch]);

  return (
    <PayrunPrepareDetail
      {...props}
      externalPayruns={data?.result?.rows ?? []}
      externalPayrunsTotal={data?.totalRecords ?? 0}
      externalPayrunsPage={externalPayrunsPage}
      onExternalPayrunsPageChange={setExternalPayrunsPage}
      isExternalPayrunsLoading={isFetching}
      refetchExternalPayruns={refetchExternalPayruns}
    />
  );
};

export default PayrunPrepareContainer;
